import type { Spine, TrackEntry } from "@esotericsoftware/spine-pixi-v8";
import type { PresentationTaskRunner } from "../runner/PresentationTaskRunner";

/**
 * Play a one-shot clip on track `track` at `runner.getTimeScale(scope)`, the rate its scoped wait runs at, so the
 * clip and the wait end together, and resolve when it ends — the `setAnimation` + `scheduleOnce(animationEnd)` pair.
 * `skipSeconds` seeks past the clip start. Resolves immediately (false) when the skeleton lacks the clip. Runner
 * choice decides the clock: the presentation runner for round beats, `chromeTaskRunner` for panel choreography.
 */
export async function playClipAndWait(
  spine: Spine,
  runner: PresentationTaskRunner,
  name: string,
  options: { track?: number; skipSeconds?: number; scope?: string } = {},
): Promise<boolean> {
  if (spine.destroyed || !spine.skeleton.data.findAnimation(name)) return false;
  const track = options.track ?? 0;
  const skip = Math.max(0, options.skipSeconds ?? 0);
  spine.state.timeScale = runner.getTimeScale(options.scope);
  const entry: TrackEntry = spine.state.setAnimation(track, name, false);
  if (skip > 0) entry.trackTime = skip;
  spine.update(0);
  const seconds = Math.max(0, (entry.animationEnd ?? 0) - skip);
  if (seconds > 0) await runner.sleep(seconds, options.scope);
  return true;
}

/**
 * Set a clip (looping or not) at the runner's time scale for `scope` (the clock's without one) without waiting;
 * returns the entry or null.
 */
export function setClip(
  spine: Spine,
  runner: PresentationTaskRunner,
  name: string,
  loop: boolean,
  track = 0,
  scope?: string,
): TrackEntry | null {
  if (spine.destroyed || !spine.skeleton.data.findAnimation(name)) return null;
  spine.state.timeScale = runner.getTimeScale(scope);
  const entry = spine.state.setAnimation(track, name, loop);
  spine.update(0);
  return entry;
}

/** Queue a clip after the current one; no-op when the skeleton lacks it. */
export function queueClip(spine: Spine, name: string, loop: boolean, track = 0, delay = 0): TrackEntry | null {
  if (spine.destroyed || !spine.skeleton.data.findAnimation(name)) return null;
  return spine.state.addAnimation(track, name, loop, delay);
}

/** The clip length in seconds, or 0 when absent. */
export function clipSeconds(spine: Spine, name: string): number {
  return spine.skeleton.data.findAnimation(name)?.duration ?? 0;
}
